import React from 'react';
import { useTranslation } from 'react-i18next';
import type { DecimalPrecision } from '@/types/common';

interface DecimalPrecisionSectionProps {
  value: DecimalPrecision;
  onChange: (value: DecimalPrecision) => void;
  disabled?: boolean;
}

const DecimalPrecisionSection: React.FC<DecimalPrecisionSectionProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation('common');
  const [raw, setRaw] = React.useState<string>(value === 'full' ? '' : String(value));
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setRaw(value === 'full' ? '' : String(value));
  }, [value]);

  const commit = (next: string) => {
    setRaw(next);
    if (next.trim() === '') {
      setError(null);
      onChange('full');
      return;
    }
    if (!/^\d+$/.test(next.trim())) {
      setError(t('common.decimalPrecision.invalidValue'));
      return;
    }
    setError(null);
    onChange(parseInt(next, 10));
  };

  return (
    <div className={`decimal-precision-section mb-4 sm:mb-6 p-3 sm:p-4 bg-muted rounded-lg border border-border ${disabled ? 'opacity-60' : ''}`}>
      <h3 className="text-sm sm:text-base md:text-lg font-semibold mb-1 sm:mb-2 text-foreground">
        {t('common.decimalPrecision.sectionTitle')}
        <span className="ml-1 text-muted-foreground text-xs font-normal">
          ({t('common.decimalPrecision.optional')})
        </span>
      </h3>
      <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3">
        {t('common.decimalPrecision.fullPrecisionHint')}
      </p>
      <div className="max-w-full sm:max-w-xs">
        <input
          type="text"
          inputMode="numeric"
          pattern="\d*"
          disabled={disabled}
          value={raw}
          onChange={(e) => commit(e.target.value)}
          placeholder={t('common.decimalPrecision.placeholder') as string}
          aria-invalid={!!error}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring ${error ? 'border-red-500' : 'border-input'} disabled:bg-muted`}
        />
        {error && (
          <p className="mt-1 text-xs text-red-600">{error}</p>
        )}
      </div>
    </div>
  );
};

export default DecimalPrecisionSection;
